import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import { useSettingsStore } from '@/store/useSettingsStore';

interface ITopClassData {
  id: string;
  name: string;
  completed_lessons: number;
}

interface ITopClassesChartProps {
  data: ITopClassData[];
}

const TopClassesChart: React.FC<ITopClassesChartProps> = ({ data }) => {
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const series = useMemo(
    () => [
      {
        name: 'Completed Lessons',
        data: data.map((item) => item.completed_lessons),
      },
    ],
    [data]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 260,
        toolbar: {
          show: false,
        },
        fontFamily: 'Aptos, sans-serif',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '113px',
          borderRadius: 8,
          borderRadiusApplication: 'end',
          distributed: false,
          dataLabels: {
            position: 'top',
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      xaxis: {
        categories: data.map((item) => item.name),
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          style: {
            colors: '#525866',
            fontSize: '14px',
            fontWeight: 400,
          },
        },
      },
      yaxis: {
        min: 0,
        tickAmount: 4,
        opposite: isRTL,
        labels: {
          style: {
            colors: '#525866',
            fontSize: '14px',
            fontWeight: 400,
          },
          formatter: (value: number) => value.toString(),
        },
      },
      fill: {
        opacity: 1,
        colors: ['#9333EA'],
      },
      grid: {
        borderColor: '#E1E4EA',
        strokeDashArray: 0,
        position: 'back',
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        style: {
          fontSize: '12px',
          fontFamily: 'Aptos, sans-serif',
        },
        custom: ({ dataPointIndex }) => {
          const classData = data[dataPointIndex];

          if (isRTL) {
            return `
              <div style="background: white; border: 1px solid #f5f7fa; border-radius: 5px; box-shadow: 0 0 18px rgba(38, 38, 38, 0.2); padding: 10px 11px; min-width: 111px; direction: rtl; text-align: right;">
                <div style="font-weight: 600; font-size: 12px; line-height: 20px; color: #9333ea; margin-bottom: 12px; font-family: Aptos, sans-serif;">
                  ${classData.name}
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; direction: rtl;">
                  <span style="font-weight: 600; font-size: 12px; line-height: 20px; color: #181b25; font-family: Aptos, sans-serif;">
                    ${classData.completed_lessons}
                  </span>
                </div>
              </div>
            `;
          }

          return `
            <div style="background: white; border: 1px solid #f5f7fa; border-radius: 5px; box-shadow: 0 0 18px rgba(38, 38, 38, 0.2); padding: 10px 11px; min-width: 111px;">
              <div style="font-weight: 600; font-size: 12px; line-height: 20px; color: #9333ea; margin-bottom: 12px; font-family: Aptos, sans-serif;">
                ${classData.name}
              </div>
              <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px;">
                <span style="font-weight: 400; font-size: 12px; line-height: 16px; color: #525866; font-family: Aptos, sans-serif;">
                  Completed Lessons
                </span>
                <span style="font-weight: 600; font-size: 12px; line-height: 20px; color: #181b25; font-family: Aptos, sans-serif;">
                  ${classData.completed_lessons}
                </span>
              </div>
            </div>
          `;
        },
      },
    }),
    [data, isRTL]
  );

  return (
    <div className="w-full" data-testid="top-classes-chart">
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={280}
      />
    </div>
  );
};

export default TopClassesChart;
